// ProgramTargetGraphScreen.tsx
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './assets/program-book.module.scss';
import AdminLayout from 'src/components/layout/RoleLayout';
import { RouteComponentProps } from 'react-router';
import { groupProgramAnswerForPlotV4 } from '../../../../../../helpers/programBook/helperPlotAnswersV4';
import Loader from '../../../../../../components/Loader';
import moment from 'moment';
import { ProgramBookApi } from '../../../../../../services/end-points';
import sortingService from '../../../../../../services/sorting';
import { useAsyncDebounce } from 'react-table';
import AbaLineChartE from '../../../../../../components/graphic/chart/AbaLineChartE';
import { DateTimePicker } from 'react-widgets';
import { toast } from 'react-toastify';

export interface StateProps {
    programBooks: any;
    programBook: any;
    programBookAnswers: any;
    inProgressAnswers: any;
}

export interface DispatchProps {
    getProgramBooks: (param: any) => void;
    getProgramBookEntity: (id: string, isHidden: string) => void;
    getProgramBookAnswers: (param: any) => void;
}

interface RouteParams {
    id: string;
    targetId: string;
}

export interface OwnProps extends RouteComponentProps<RouteParams> {}
export interface Props extends StateProps, DispatchProps, OwnProps {}

const MemoAbaLineChartE = React.memo(AbaLineChartE);

function findTargetAnswersInTree(tree: any[], targetId: number) {
    if (!Array.isArray(tree)) return null;

    for (const section of tree) {
        const domains = section?.dataDomain ?? [];
        for (const domain of domains) {
            const longTerms = domain?.dataLongTerm ?? [];
            for (const longTerm of longTerms) {
                const shortTerms = longTerm?.dataShortTerm ?? [];
                for (const shortTerm of shortTerms) {
                    const answersArr = shortTerm?.answers ?? [];
                    for (const ta of answersArr) {
                        const tid = ta?.target?.id;
                        if (Number(tid) === Number(targetId)) return ta;
                    }
                }
            }
        }
    }
    return null;
}

const ProgramTargetGraphScreen: React.FunctionComponent<Props> = (props: Props) => {
    const {
        match: {
            params: { id, targetId },
        },
        programBook,
        getProgramBookEntity,
        inProgressAnswers,
    } = props;

    const targetIdNum = useMemo(() => Number(targetId), [targetId]);

    const [isHidden, setIsHidden] = useState<'0' | '1'>('0');
    const [isLoad, setIsLoad] = useState(false);

    const [globalFilters, setGlobalFilters] = useState({
        dateFrom: moment().add(-100, 'years').format('YYYY-MM-DD'),
        dateTo: moment().format('YYYY-MM-DD'),
    });

    const [answersByTargetId, setAnswersByTargetId] = useState<Record<number, any[]>>({});

    const onChange = useAsyncDebounce((change) => {
        setGlobalFilters((prev) => ({ ...prev, [change.id]: change.value }));
    }, 200);

    // 1) ProgramBook (unchanged)
    useEffect(() => {
        if (!programBook) {
            getProgramBookEntity(id, '1');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // 2) Load answers for a single targetId with the same request
    useEffect(() => {
        if (!id || !targetIdNum) return;

        let cancelled = false;

        (async () => {
            try {
                setIsLoad(true);

                const response2 = await ProgramBookApi.getProgramBookAnswersForPlot({
                    ...{
                        'programBook.id': id,
                        dateFrom: globalFilters.dateFrom,
                        dateTo: globalFilters.dateTo,
                        programTarget: [targetIdNum],
                        'axios-retry': { retries: 3 },
                    },
                    order: sortingService.makeOrder({}),
                    page: 1,
                    itemsPerPage: 100000,
                    pagination: 1,
                });

                const ss: any[] = response2.data?.data ? JSON.parse(response2.data?.data) : [];

                if (cancelled) return;

                setAnswersByTargetId((prev) => ({
                    ...prev,
                    [targetIdNum]: ss,
                }));
            } catch (e) {
                console.error(e);
                toast.error(e?.message ?? 'Failed to load target answers');
            } finally {
                if (!cancelled) setIsLoad(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [id, targetIdNum, globalFilters.dateFrom, globalFilters.dateTo]);

    // 3) Build targetAnswers via the helper, with NO setState/loops
    const targetAnswers = useMemo(() => {
        if (!programBook) return null;

        // Only one target is ever "open"
        const isHideButton = [targetIdNum];

        // On the single-target page groupedByTarget can just be an empty object
        const groupedByTarget = {};

        const { tree } = groupProgramAnswerForPlotV4(
            programBook,
            answersByTargetId,
            globalFilters,
            isHideButton,
            groupedByTarget,
        );

        return findTargetAnswersInTree(tree, targetIdNum);
    }, [programBook, answersByTargetId, globalFilters.dateFrom, globalFilters.dateTo, targetIdNum]);

    function renderPhaseInfo(phase: any) {
        return (
            <span className={styles.phaseItem} key={phase?.id || `${phase?.title}-${phase?.startDate}`}>
                <b>{phase?.title}:</b>{' '}
                {phase?.startDate ? moment(phase?.startDate, 'YYYY-MM-DD HH:mm:ss').format('ll') : ''}
                {phase?.endDate ? ' - ' + moment(phase?.endDate, 'YYYY-MM-DD HH:mm:ss').format('ll') : ''}
                {'; '}
            </span>
        );
    }

    const hasAnswers = !!(targetAnswers?.answers?.length > 0);

    return (
        <AdminLayout>
            {!programBook || inProgressAnswers ? <Loader /> : null}
            <div className={classNames(styles.wrapForm)}>
                <div>
                    <div className={classNames(styles.firstFilterBlock)}>
                        <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                            <span>From:</span>
                            <DateTimePicker
                                value={globalFilters.dateFrom ? moment(globalFilters.dateFrom).toDate() : undefined}
                                onChange={(date: any) => {
                                    onChange({
                                        id: 'dateFrom',
                                        value: date ? moment(date).format('YYYY-MM-DD') : undefined,
                                    });
                                }}
                                time={false}
                                date={true}
                                format={'MMM D, YYYY'}
                            />
                        </div>

                        <div className={classNames(styles.itemFilterBlock, 'mb-3 mr-3')}>
                            <span>To: </span>
                            <DateTimePicker
                                value={globalFilters.dateTo ? moment(globalFilters.dateTo).toDate() : undefined}
                                onChange={(date: any) => {
                                    onChange({
                                        id: 'dateTo',
                                        value: date ? moment(date).format('YYYY-MM-DD') : undefined,
                                    });
                                }}
                                time={false}
                                date={true}
                                format={'MMM D, YYYY'}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.plotAnswerContainer}>
                    {isLoad ? (
                        <Loader />
                    ) : !targetAnswers ? (
                        <div>No data for this target.</div>
                    ) : (
                        <div
                            key={'target-' + targetAnswers?.target?.id}
                            className={classNames(
                                styles.targetContainer,
                                isHidden === '0' && targetAnswers?.target?.isHidden == true ? styles.hiddenTarget : {},
                            )}
                        >
                            <h2>
                                {targetAnswers?.target?.title ? targetAnswers.target.title : 'No title'}
                                {targetAnswers?.target?.type == 2 ? ', seconds' : ''}
                                {targetAnswers?.target?.isHidden == true ? <b>(hidden)</b> : ''}
                            </h2>

                            {targetAnswers?.target?.prompt &&
                            (targetAnswers?.target?.type == 1 || targetAnswers?.target?.type == 3) ? (
                                <div className={styles.targetDescription}>
                                    Prompt:<b>{' ' + targetAnswers.target.prompt}</b>
                                </div>
                            ) : null}

                            <div className={styles.targetDescription}>{targetAnswers?.target?.description}</div>

                            <div className={styles.phaseItems}>
                                {targetAnswers?.target?.isPhased &&
                                targetAnswers?.target?.programTargetPhases?.length ? (
                                    <div>
                                        <b>Phases:</b>
                                    </div>
                                ) : null}

                                {targetAnswers?.target?.isPhased
                                    ? targetAnswers.target.programTargetPhases?.map((phase: any) =>
                                          renderPhaseInfo(phase),
                                      )
                                    : null}
                            </div>

                            {hasAnswers ? (
                                <MemoAbaLineChartE
                                    title={targetAnswers?.target?.title ? targetAnswers.target.title : 'No title'}
                                    yLabel="Answers"
                                    points={targetAnswers.formatAnswers}
                                    phases={targetAnswers.phases}
                                    isShowHundred={targetAnswers?.target?.type == 1 || targetAnswers?.target?.type == 3}
                                />
                            ) : (
                                <div>This target has no answers yet.</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default ProgramTargetGraphScreen;
